import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService, SymbolLoadService, EventsService, QuoteStoreService, SubjectType, NgxToastrService, SymbolSearchService } from '@app/_shared';
import { of, Observable, Subject } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { AlertsResponse, AlertsObj, MarketSecurityData } from './alerts.interfaces';
import { AlertsModel } from './alerts.models';
import { CustomEvents, SELECTED_VENDOR, Utils } from '@app/_helpers';
import { BooksSocketService } from '@app/_shared/providers/websocket/books-socket/books-socket.service';
import { SIDEBAR_BUBBLE_EVENTS, ORDER_CONDITION } from '@app/_shared/shared.enums';
import { STATUS } from '@app/_shared/modules/order-form/order-form.enums';
import { WebAlertStatus } from '../../../../../../../utProto/webAlertStructureEnums_pb';
import { startCase } from 'lodash-es';
import { getLocale } from '@app/_shared/pipes/locale-converter/locale-converter.pipe';

declare const symbol_info;

enum ALERT_ACTIONS {
    REJECT = 'rejected',
    RAISE = 'raised',
    ACCEPT = 'accepted'
}
@Injectable({
    providedIn: 'root'
})
export class AlertsService {

    showAlertbadge = false;
    alertsObj: AlertsResponse[] = [];
    alertUpdate = new Subject();
    uniqueMarketSecurity: MarketSecurityData[] = [];
    constructor(
        private http: HttpClient,
        private configServices: ConfigurationService,
        private symbolLoadService: SymbolLoadService,
        private booksSocketService: BooksSocketService,
        private eventService: EventsService,
        private quoteStoreService: QuoteStoreService,
        private snackBar: NgxToastrService,
        private symbolSearchService: SymbolSearchService
    ) {

        this.booksSocketService.getSubject(SubjectType.ALERTS).subscribe((alerts: AlertsResponse[]) => {
            this.updateAlerts(alerts);
        });

        this.eventService.subscribe(CustomEvents.SideBarNotification, label => {
            if (label === SIDEBAR_BUBBLE_EVENTS.ALERT) {
                this.showAlertbadge = true;
            }
        });

        this.eventService.subscribe(CustomEvents.HideNotificationBadge, label => {
            if (label === SIDEBAR_BUBBLE_EVENTS.ALERT) {
                this.showAlertbadge = false;
            }
        });
    }
    getUniqueMarketSecurity(alert: AlertsResponse) {
        let newVal = true;
        this.uniqueMarketSecurity.forEach(el => {
            if (el.market_id === alert.market_id && el.security_id === alert.security_id) {
                newVal = false;
            }
        });

        if (newVal) {
            this.uniqueMarketSecurity.push({
                market_id: alert.market_id,
                security_id: alert.security_id
            })
        }
    }

    getKey(alert: AlertsResponse) {
        return `${alert.market_id}_${alert.security_id}`;
    }

    markAsSeen(id: number) {
        this.alertsObj.forEach((obj, index) => {
            if (obj.id === id) {
                this.alertsObj[index].condition_status = WebAlertStatus.WEBALERTSTATUS_ACCEPT;
                this.alertUpdate.next();
            }
        });
        this.checkBadge();
    }

    markAsTimeout(id: number) {
        this.alertsObj.forEach((obj, index) => {
            if (obj.id === id) {
                this.alertsObj[index].condition_status = WebAlertStatus.WEBALERTSTATUS_TIMEOUT;
                this.alertUpdate.next();
            }
        });
        this.checkBadge();
    }

    checkBadge() {
        if (!this.alertsObj.filter(el => el.condition_status == WebAlertStatus.WEBALERTSTATUS_PENDING).length) {
            this.eventService.publish(CustomEvents.HideNotificationBadge, SIDEBAR_BUBBLE_EVENTS.ALERT);
        }
    }

    removeFromArray(alertObj: AlertsResponse) {
        this.alertsObj.splice(
            this.alertsObj.indexOf(
                this.alertsObj.find((el) => {
                    return el.id === alertObj.id
                })
            ), 1);
    }

    insertToArray(alertObj: AlertsModel) {
        let newData = true;
        this.alertsObj.forEach((obj, idx) => {
            if (obj.id === alertObj.id) {
                newData = false;
                this.alertsObj[idx] = alertObj;
            }
        });

        if (newData) {
            this.alertsObj.push(alertObj);
        }
    }

    /**
     * Function to generate Snackbar based on parameters
     * @param alertsObj The alert's Object
     * @param action Used in string generation for snackbar.
     */
    openSnackBar(alertsObj: AlertsResponse, action: ALERT_ACTIONS) {
        let orderCondition = ORDER_CONDITION[alertsObj.condition];
        orderCondition = startCase(orderCondition);
        let snackBarString = `${orderCondition} Alert ${action} for Symbol '${alertsObj.scrip_name.toUpperCase()}' by Algo '
                ${alertsObj.algo_name.toUpperCase()}' for Lots: ${getLocale(alertsObj.lots, 0)}`
        snackBarString += alertsObj.price ? ` @ Price ${getLocale(alertsObj.price, 2)}` : ' @ Market Price';
        this.snackBar.simpleCustomToaster(snackBarString, false);
    }

    updateAlerts(alerts: AlertsResponse[]) {
        alerts.forEach(alert => {
            this.getUniqueMarketSecurity(alert);
            const alertsObj = new AlertsModel(alert);
            const key = this.getKey(alert);
            alertsObj.setSymbolInfo(this.symbolSearchService.symbolInfo(key));

            if (alert.condition_status === WebAlertStatus.WEBALERTSTATUS_ACCEPT) {
                this.markAsSeen(alert.id);
            } else if (alert.condition_status === WebAlertStatus.WEBALERTSTATUS_REJECT) {
                this.markAsTimeout(alert.id);
            } else if (alert.condition_status === WebAlertStatus.WEBALERTSTATUS_INVALID) {
                this.checkBadge();
                this.snackBar.simpleCustomToaster(alert.Message)
            } 
            else {
                const quoteObj = this.quoteStoreService.getFeedObj(alert.market_id, alert.security_id);
                alertsObj.listenLtp(quoteObj);
                this.insertToArray(alertsObj)
                if (alertsObj.condition_status === WebAlertStatus.WEBALERTSTATUS_PENDING) {
                    this.openSnackBar(alertsObj, ALERT_ACTIONS.RAISE)
                }
            }
        });
        this.alertUpdate.next();
    }

    get pendingAlerts() {
        return this.alertsObj.filter(al => al.condition_status === WebAlertStatus.WEBALERTSTATUS_PENDING);
    }
}
